<template>
  <div class="q-pa-lg">
    <div class="text-125 text-bold q-mb-md">How your bill is calculated for {{ usage }} kWh Usage:</div>
    <div class="q-mb-md text-100 text-blue-grey-8">
      <span class="text-bold">NOTE</span>: Electricity pricing in Texas has two parts to it:
      <span class="text-bold">Provider Charges ({{ plan.product.brand.name }})</span> and
      <span class="text-bold">Utility Delivery Charges ({{ plan.tdsp.short_name }})</span>.
      <span v-if="is_bundled">
          For simplicity, this plan combines these two charge types. Utility Delivery Charges are already built into the Bundled Charges shown below.
        </span>
      <span v-else>
          These combine to become
          <span class="text-bold">Total Charges</span>.
        </span>
    </div>
  </div>

  <q-markup-table separator="horizontal" flat wrap-cells class="">
    <tbody>
    <tr class="bg-grey-1">
      <td colspan="4">
        <div class="text-125">

          <div v-if="!is_bundled">
            <span class="text-bold">Provider Charges </span>
            <span>({{ plan.product.brand.name }})</span>
          </div>
          <div v-else>
            <div class="text-bold">Bundled Charges</div>
            <span>{{ plan.product.brand.name }} + {{ plan.tdsp.short_name }} Delivery Charges Combined</span>
          </div>
        </div>
      </td>
    </tr>
    <tr v-for="charge in provider_charges">
      <td style="vertical-align: bottom; width: 20%">
        <div class="text-100">
          {{ charge_name(charge) }}
        </div>
      </td>
      <td style="vertical-align: bottom">
        <div class="text-100">
          <div v-html="charge_description(charge)"></div>
          <div v-if="charge_details(charge)" v-html="charge_details(charge)"></div>
        </div>
      </td>
      <td style="vertical-align: bottom; text-align: right">
        <div class="text-100">$</div>
      </td>
      <td class="text-right" style="vertical-align: bottom; width: 10%">
        <div class="text-100">
          {{ format_dollar_amount(charge.amount) }}
        </div>
      </td>
    </tr>
    <tr v-if="!is_bundled">
      <td colspan="3" style="border-top: 2px solid #0000001a;">
        <div class="text-right text-100 text-bold">
          Provider Charges Subtotal: &emsp; $
        </div>
      </td>
      <td style="border-top: 2px solid #0000001a;">
        <div class="text-right text-100 text-bold">
          {{ provider_total }}
        </div>
      </td>
    </tr>
    <!--    </tbody>-->
    <!--  </q-markup-table>-->

    <!--  <q-markup-table separator="horizontal" flat  wrap-cells class="q-mt-md">-->
    <!--    <tbody>-->
    <tr class="bg-grey-1" v-if="!is_bundled">
      <td colspan="5">
        <div class="text-125">
          <span class="text-bold">Utility Delivery Charges </span>
          <span>({{ plan.tdsp.short_name }})</span>
        </div>
      </td>
    </tr>
    <tr v-for="charge in utility_charges" v-if="!is_bundled">
      <td style="vertical-align: bottom">
        <div class="text-100">
          {{ charge_name(charge) }}
        </div>
      </td>
      <td style="vertical-align: bottom">
        <div class="text-100">
          <div v-html="charge_description(charge)"></div>
          <div v-if="charge_details(charge)" v-html="charge_details(charge)"></div>
        </div>
      </td>
      <td style="vertical-align: bottom; text-align: right">
        <div class="text-100">$</div>
      </td>
      <td class="text-right" style="vertical-align: bottom">
        <div class="text-100">
          {{ format_dollar_amount(charge.amount) }}
        </div>
      </td>
    </tr>
    <tr v-if="!is_bundled">
      <td colspan="3" style="border-top: 2px solid #0000001a;">
        <div class="text-right text-100 text-bold">
          Utility Delivery Charges Subtotal: &emsp; $
        </div>
      </td>
      <td style="border-top: 2px solid #0000001a;">
        <div class="text-right text-100 text-bold">
          {{ utility_total }}
        </div>
      </td>
    </tr>
    <!--    </tbody>-->
    <!--  </q-markup-table>-->

    <!--  <q-separator></q-separator>-->

    <!--  <q-markup-table separator="none" flat wrap-cells class="bg-grey-2 q-py-sm" style="border-top: 3px double #0000001a">-->
    <!--    <tbody>-->
    <tr class="bg-grey-2">
      <td colspan="3" style="border-top: 3px double #0000001a">
        <div class="text-125 text-bold text-right q-py-md">
          Total Charges*: &emsp; $
        </div>
      </td>
      <td style="border-top: 3px double #0000001a">
        <div class="text-125 text-bold text-right">
          {{ format_dollar_amount(display_pricing.total) }}
        </div>
      </td>
    </tr>
    <tr class="bg-grey-2">
      <td colspan="4" class="responsive_text text-right">
        The <strong>average price</strong> you paid for electric service this month
        <strong>
          (${{ format_dollar_amount(display_pricing.total) }} ÷ {{ usage }} kWh):
          {{ display_pricing.avg_cents.toFixed(1) }}¢
        </strong>
      </td>
    </tr>
    </tbody>
  </q-markup-table>

  <q-separator></q-separator>

  <q-card-section>
    <div class="text-blue-grey-8 text-075">
      *These estimates include all the recurring charges you'll see on your bill excluding government fees and taxes.
      Estimates are based on information provided by {{ plan.product.brand.name }}. These estimates should be
      confirmed using the information found in the Electricity Facts Label and Terms of Service. Actual price will
      vary according to your actual usage.
    </div>
  </q-card-section>

</template>

<script>
import { computed } from "vue";
import Utilities from "src/composables/Utilities";

export default {
  name: "PricingCalcs",
  props: {
    plan: {
      type: Object,
      required: true
    },
    usage: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    const display_pricing = computed(() => props.plan[`display_pricing_${ props.usage }`])
    const charges = computed(() => display_pricing.value?.charges || []);
    const provider_charges = computed(() => charges.value.filter(x => !x.component?.tdsp_charge))
    const provider_total = computed(() => provider_charges.value.reduce((prev, curr) => prev + curr.amount, 0).toFixed(2))
    const utility_charges = computed(() => charges.value.filter(x => x.component?.tdsp_charge).sort((a, b) => a.component.multiplicative > b.component.multiplicative ? 1 : -1))
    const utility_total = computed(() => utility_charges.value.reduce((prev, curr) => prev + curr.amount, 0).toFixed(2))
    const is_bundled = computed(() => !utility_charges.value.length);

    const { format_dollar_amount } = Utilities();

    const charge_name = function (charge) {
      if (!charge.component) {
        return 'PUC Charge';
      }
      else {
        if (charge.component.multiplicative) {
          if (charge.component.amount >= 0) {
            if (charge.component.tdsp_charge) {
              return 'Delivery Charge'
            }
            if (charge.component.description) {
              return charge.component.description;
            }
            return 'Energy Charge'
          }
          else {
            return 'Discount';
          }
        }
        else {
          if (charge.component.amount >= 0) {
            if (charge.component.min >= 1) {

            }
            return ((charge.component.min === 0 || charge.component.min === 1) && !charge.component.max) ?
                (charge.component.tdsp_charge) ? 'Base Charge' : 'Base Charge'
                : 'Usage Charge';
          }
          else {
            return 'Bill Credit';
          }
        }
      }
    }
    const charge_description = function (charge) {
      if (!charge.component) {
        return `$${ charge.total } X ${ charge.puc_percentage }`;
      }
      if (charge.component.multiplicative) {
        if (charge.component.percentage && charge.component.amount <= 0) {
          return `${ Math.round(charge.component.percentage * 10000) / 100 }% discount from facts label`
        }

        const { should_calc, calc_usage, component } = charge;
        const { min, max, amount } = component;
        const rounded = Math.round(charge.component.amount * 1000000) / 10000;

        if (min >= 0 && !max) {
          if (min > 1) {
            return `${ rounded }¢ per kWh for usage greater than or equal to ${ min } kWh`
          }
          else {
            return `${ rounded }¢ per kWh`;
          }
        }
        else if (min >= 0 && max) {
          return `${ rounded }¢ per kWh for usage between ${ min } and ${ max } kWh`
        }
        else if (min >= 0 && !max) {
          return `${ rounded }¢ per kWh for usage less than ${ max } kWh`
        }
      }
      else {
        let { min, max } = charge.component;
        let amount_value = Math.round(Math.abs(charge.component.amount) * 100) / 100;
        let charge_type = '';
        if (charge.component.amount >= 0) {
          charge_type = 'charge'
        }
        else {
          charge_type = 'credit'
        }
        if (max) {
          return `<i>($${ amount_value } ${ charge_type } if usage is between ${ min } and ${ max } kWh)</i>`;
        }
        else if (min > 1) {
          return `<i>($${ amount_value } ${ charge_type } if usage at least ${ min })</i>`
        }
        else {
          return `<i>($${ amount_value } ${ charge_type } per billing cycle)</i>`
        }
      }
    }
    const charge_details = function (charge) {
      if (charge.component && !charge.component.multiplicative) {

        const { should_calc, calc_usage, component } = charge;
        const { min, max, amount } = component;

        if ((min === 0 || min === 1) && !max) {
          return null
        }

        const amount_value = should_calc ? Math.abs(amount) : 0;
        const type = amount < 0 ? 'credit' : 'charge';
        const suffix = max ? ` between ${ min } and ${ max } kWh` : ` at least ${ min } kWh`
        const decision = should_calc ? 'is' : 'is not'

        return `$${ amount_value } ${ type } (<strong>${ display_pricing.value.usage } kWh</strong> <strong><u>${ decision }</u></strong>${ suffix })`;
      }
      else {
        const { should_calc, calc_usage, component } = charge;
        const { min, max, amount } = component;

        const rounded = Math.round(charge.component.amount * 1000000) / 10000;
        if (charge.component.percentage && charge.component.percentage < 1) {
          return `${ rounded }¢ x ${ charge.component.percentage } x <strong>${ charge.calc_usage } kWh`
        }
        else {
          return `${ rounded }¢ x <strong>${ charge.calc_usage } kWh`
        }
      }
      return null;
    }

    return {

      is_bundled,

      display_pricing,

      provider_charges,
      provider_total,

      utility_charges,
      utility_total,

      charge_name,
      charge_details,
      charge_description,

      format_dollar_amount
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
